import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import * as generic from '../../../components/Generico';
import { AccionistaService } from '../../../service/AccionistaService';
import { InputComponent } from '../../../components/InputComponent';
import { Panel } from 'primereact/panel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Divider } from 'primereact/divider';
import { AccionistaEconomica } from '../../../models/ficha/AccionistaEconomica';
import { BottomActionBar } from '../../../components/ui/BottomActionBar';
import { InputActividadEconomica } from '../../../components/ui/Formularios/InputActividadEconomica';

export const InformacionEconomica = ({ toast, back, next, id, permss, soloLectura, catalogos }) => {

    const omodel = new AccionistaEconomica({ tipoFrmEmpresa: catalogos?.tipoFrmEmpresa });
    const defaultValues = generic.funDefault(omodel.model);
    const { control, formState: { errors }, handleSubmit, reset, setError, getValues, setValue, watch } = useForm({ defaultValues });
    const accionistaServ = new AccionistaService();
    const { inNumber, inText } = InputComponent({ omodel, errors, control, permss, id, soloLectura });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [tieneConyugue, setTieneConyugue] = useState(false);
    const otrosIngresos = watch('otros_ingresos');
    const otrosIngresosCony = watch('otros_ingresos_cony');

    useEffect(() => {
        editarFicha();
        // eslint-disable-next-line
    }, [id]);

    const editarFicha = () => {
        if (id == null) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        accionistaServ.consultarAccionista(id, 'seccion2').then(res => {
            if (!res.data.accionista) {
                return;
            }
            const _accionista = res.data.accionista;
            const _cats = catalogos;
            setTieneConyugue(_accionista.estado_civil === _cats?.casado?.cat_id || _accionista.estado_civil === _cats?.union?.cat_id);

            reset(generic.renewValues(_accionista, omodel.model));
        }).finally(() => setIsLoading(false));
    }

    const onSubmit = (data) => {
        setIsSaving(true);
        data.seccion = 'seccion2';
        data.id = id;
        data.tipoFrmEmpresa = catalogos?.tipoFrmEmpresa;
        data.tieneConyugue = tieneConyugue;
        accionistaServ.editarAccionista(data).then(res => {
            generic.funcSuccess(res, toast);
            next();
        }).catch(error => {
            generic.funcError(error, toast, setError);
        }).finally(() => setIsSaving(false));
    }

    const onError = () => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Se detectaron errores, por favor revise el formulario para corregir.', life: 3000 });
    }

    const actualizarPatrimonio = () => {
        const actvs = ['ACTV_INMUEBLES', 'ACTV_MUEBLES', 'ACTV_EFECTIVO', 'ACTV_INVERSIONES', 'ACTV_CREDITOS', 'ACTV_OTROS'];
        const pasvs = ['PASV_BANCARIOS', 'PASV_HIPOTECARIOS', 'PASV_QUIROGRAFARIO', 'PASV_CREDITO', 'PASV_OTRAS_CTAS', 'PASV_OTROS_PASV'];
        let totalPasivos = 0;
        let totalActivos = 0;
        actvs.forEach(itm => {
            if (getValues(itm) && !isNaN(getValues(itm))) {
                totalActivos += parseFloat(getValues(itm));
            }
        });
        pasvs.forEach(itm => {
            if (getValues(itm) && !isNaN(getValues(itm))) {
                totalPasivos += parseFloat(getValues(itm));
            }
        });
        setValue('total_activos', totalActivos);
        setValue('total_pasivos', totalPasivos);
        setValue('total_patrimonio', totalActivos - totalPasivos);
    }

    if (isLoading) {
        return (
            <div className="flex flex-column align-items-center justify-content-center p-5">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
                <span className="mt-3 text-color-secondary font-medium text-lg">Consultando información...</span>
            </div>
        );
    }

    return (
        <form id="infEconomicaAccionistaForm" onSubmit={handleSubmit(onSubmit, onError)} className="p-fluid">
            <Panel header={generic.renderPanelHeader('3', 'INFORMACIÓN ECONÓMICA', 'pi pi-chart-line')} className="panel-ficha shadow-1 mb-4">
                <div className="col-12 mb-3">
                    <div className="flex align-items-center">
                        <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                            <i className="pi pi-user text-primary"></i>
                            INGRESOS Y EGRESOS DEL ACCIONISTA
                        </span>
                        <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                    </div>
                </div>

                <div className="formgrid grid">
                    {inNumber('ingreso_mensual_rel_dep', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left' }, otrosIngresos > 0 ? 4 : 6)}
                    {inNumber('otros_ingresos', 2, true, { icon: 'pi pi-plus-circle', iconPosition: 'left' }, otrosIngresos > 0 ? 4 : 6)}
                    {otrosIngresos > 0 && inText('fuente_ingresos', { icon: 'pi pi-briefcase' }, 4)}
                    {omodel.model.otra_actividad_economica.table && (
                        <InputActividadEconomica name="otra_actividad_economica" control={control} modelDef={omodel.model['otra_actividad_economica']} disabled={soloLectura} col={12} />
                    )}
                    {inNumber('gasto_mensual', 2, true, { icon: 'pi pi-arrow-down-right', iconPosition: 'left' }, 12)}
                </div>

                {tieneConyugue && (
                    <>
                        <div className="col-12 mb-3 mt-4">
                            <div className="flex align-items-center">
                                <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                                    <i className="pi pi-heart text-primary"></i>
                                    INGRESOS DEL CÓNYUGE / CONVIVIENTE
                                </span>
                                <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                            </div>
                        </div>

                        <div className="formgrid grid">
                            {inNumber('in_mensual_rel_dep_cony', 2, true, { icon: 'pi pi-arrow-up-right', iconPosition: 'left' }, otrosIngresosCony > 0 ? 4 : 6)}
                            {inNumber('otros_ingresos_cony', 2, true, { icon: 'pi pi-plus-circle', iconPosition: 'left' }, otrosIngresosCony > 0 ? 4 : 6)}
                            {otrosIngresosCony > 0 && inText('fuente_ingresos_cony', { icon: 'pi pi-briefcase' }, 4)}
                            {omodel.model.otra_actividad_economica_cony.table && (
                                <InputActividadEconomica name="otra_actividad_economica_cony" control={control} modelDef={omodel.model['otra_actividad_economica_cony']} disabled={soloLectura} col={12} />
                            )}
                        </div>
                    </>
                )}
            </Panel>

            <Panel header={generic.renderPanelHeader('4', 'DECLARACIÓN PATRIMONIAL', 'pi pi-wallet')} className="panel-ficha shadow-1 mb-4">
                <div className="grid formgrid mt-2">
                    <div className="col-12 md:col-6 mb-3 md:mb-0">
                        <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1 border-1 border-300">
                            <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">ACTIVOS</div>
                            <div className="formgrid grid flex-grow-1 align-content-start">
                                {inNumber('ACTV_INMUEBLES', 2, true, { icon: 'pi pi-home', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('ACTV_MUEBLES', 2, true, { icon: 'pi pi-car', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('ACTV_EFECTIVO', 2, true, { icon: 'pi pi-money-bill', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('ACTV_INVERSIONES', 2, true, { icon: 'pi pi-chart-line', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('ACTV_CREDITOS', 2, true, { icon: 'pi pi-credit-card', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('ACTV_OTROS', 2, true, { icon: 'pi pi-box', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                            </div>

                            <div className="mt-3">
                                <Divider type="dashed" className="my-2" />
                                <div className="bg-blue-50 p-2 border-round border-1 border-blue-200 formgrid grid mx-0">
                                    {inNumber('total_activos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true }, 12)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <div className="p-3 surface-50 border-round h-full flex flex-column shadow-1 border-1 border-300">
                            <div className="text-center text-primary font-bold mb-3 border-bottom-1 pb-2 text-lg">PASIVOS</div>
                            <div className="formgrid grid flex-grow-1 align-content-start">
                                {inNumber('PASV_BANCARIOS', 2, true, { icon: 'pi pi-building-columns', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('PASV_HIPOTECARIOS', 2, true, { icon: 'pi pi-home', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('PASV_QUIROGRAFARIO', 2, true, { icon: 'pi pi-file', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('PASV_CREDITO', 2, true, { icon: 'pi pi-credit-card', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('PASV_OTRAS_CTAS', 2, true, { icon: 'pi pi-list', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                                {inNumber('PASV_OTROS_PASV', 2, true, { icon: 'pi pi-box', iconPosition: 'left', func: actualizarPatrimonio }, 12)}
                            </div>

                            <div className="mt-3">
                                <Divider type="dashed" className="my-2" />
                                <div className="bg-blue-50 p-2 border-round border-1 border-blue-200 formgrid grid mx-0">
                                    {inNumber('total_pasivos', 2, true, { icon: 'pi pi-chart-bar', iconPosition: 'left', disabled: true }, 12)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid mt-4">
                    <div className="col-12 md:col-6 md:col-offset-3">
                        <div className="bg-blue-100 p-3 border-round border-1 border-blue-300 shadow-2 formgrid grid mx-0">
                            {inNumber('total_patrimonio', 2, true, { icon: 'pi pi-wallet', iconPosition: 'left', disabled: true, min: 'nulo' }, 12)}
                        </div>
                    </div>
                </div>
            </Panel>

            <BottomActionBar onPrev={back} onNext={next} formId="infEconomicaAccionistaForm" loading={isSaving} isReadOnly={soloLectura} />
        </form>
    );
}
